import { parseToHsla, hsla, toHex } from 'color2k';

/**
 * @file invertColors.js
 * Mirrors the colors an email carries so the sender's light-mode design stays
 * readable when the dashboard is in dark mode.
 *
 * Emails ship their own palette — white table backgrounds, near-black text —
 * and we render them inside a dark bubble. Whatever the email leaves unstyled
 * inherits our light-on-dark text color, so a white `bgcolor` ends up with
 * white text sitting on it. Mirroring every color the email declares fixes
 * both halves at once: light backgrounds go dark, dark text goes light.
 *
 * The mirror flips HSL lightness (`l` -> `1 - l`). For greys that is exactly
 * "the distance from white becomes the distance from black" — white becomes
 * black, black becomes white — while keeping hue and saturation, so a green
 * "Pro:" heading stays green instead of turning magenta the way a raw
 * `255 - channel` inversion would leave it.
 */

/**
 * Inline style properties whose value can carry a color. Shorthands are listed
 * alongside the longhands because emails lean on them heavily
 * (`border: solid #eeeeee 8px`) and not every engine expands them for us.
 */
const COLOR_STYLE_PROPS = new Set([
  'color',
  'background',
  'background-color',
  'background-image',
  'border',
  'border-color',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline',
  'outline-color',
  'box-shadow',
  'text-shadow',
  'text-decoration',
  'text-decoration-color',
  'column-rule',
  'column-rule-color',
  'caret-color',
  'fill',
  'stroke',
]);

/** Presentational attributes that predate CSS but are still all over emails. */
const COLOR_ATTRIBUTES = ['bgcolor', 'color', 'bordercolor'];

/**
 * Color-bearing tokens inside a declaration value. `url(...)` is matched first
 * so an asset path such as `tan.png` is never mistaken for the `tan` keyword,
 * and the color functions come before the bare-word branch so `rgb(1, 2, 3)`
 * is taken whole rather than as the word `rgb`.
 */
const VALUE_TOKEN =
  /url\([^)]*\)|#[0-9a-f]{3,8}\b|\b(?:rgba?|hsla?)\([^)]*\)|\b[a-z]{3,}\b/gi;

/**
 * @param {string} color - Any CSS color notation, or a value that is not one
 * @returns {string|null} The mirrored color, or null when `color` is not a
 * color, or is fully transparent and so has nothing to mirror
 */
export const invertColor = color => {
  try {
    const [hue, saturation, lightness, alpha] = parseToHsla(color);
    if (alpha === 0) return null;
    const mirrored = hsla(hue, saturation, 1 - lightness, alpha);
    // Hex reads closer to what the email author wrote; hsla() keeps the alpha.
    return alpha === 1 ? toHex(mirrored) : mirrored;
  } catch {
    // Not a color: `solid`, `inherit`, a length, a font name, ...
    return null;
  }
};

/**
 * @param {string} value - A declaration value, possibly a shorthand or gradient
 * @returns {string} The value with every color token mirrored
 */
const invertColorsInValue = value =>
  value.replace(VALUE_TOKEN, token =>
    token.toLowerCase().startsWith('url(')
      ? token
      : (invertColor(token) ?? token)
  );

const invertStyleAttribute = el => {
  const props = [];
  for (let index = 0; index < el.style.length; index += 1) {
    const prop = el.style.item(index);
    if (COLOR_STYLE_PROPS.has(prop)) props.push(prop);
  }

  props.forEach(prop => {
    const value = el.style.getPropertyValue(prop);
    const inverted = invertColorsInValue(value);
    if (inverted !== value) {
      el.style.setProperty(prop, inverted, el.style.getPropertyPriority(prop));
    }
  });
};

export const isColor = value => {
  try {
    parseToHsla(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * `bgcolor` and friends predate CSS, and browsers happily read a bare hex
 * triplet from them (`bgcolor="ffffff"`). Retry with the `#` so those legacy
 * attributes — which is how most bulk mailers still paint their tables — are
 * read as the color the browser paints instead of being skipped.
 *
 * @param {string} value - An HTML presentational color attribute value
 * @returns {string|null} The value as a CSS color, or null when it is not one
 */
export const parseLegacyColor = value => {
  const color = value.trim();
  if (!color) return null;
  if (isColor(color)) return color;
  return /^[0-9a-f]{3,8}$/i.test(color) && isColor(`#${color}`)
    ? `#${color}`
    : null;
};

/**
 * @param {string} value - An HTML presentational color attribute value
 * @returns {string|null} The mirrored color, or null when it is not a color
 */
const invertLegacyColor = value => {
  const color = parseLegacyColor(value);
  return color ? invertColor(color) : null;
};

const invertColorAttributes = el => {
  COLOR_ATTRIBUTES.forEach(attr => {
    const value = el.getAttribute(attr);
    if (!value) return;
    const inverted = invertLegacyColor(value);
    if (inverted) el.setAttribute(attr, inverted);
  });
};

/**
 * Mirror every color declared in a fragment of email HTML. Images are left
 * alone — a logo drawn for a white background is better off looking out of
 * place than looking like a negative.
 *
 * @param {string} html - Sanitized email HTML
 * @returns {string} The same HTML with its colors mirrored for dark mode
 */
export const invertEmailColors = html => {
  if (!html || typeof html !== 'string') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('*').forEach(el => {
    invertColorAttributes(el);
    if (el.hasAttribute('style')) invertStyleAttribute(el);
  });

  return doc.body.innerHTML;
};

export default invertEmailColors;
